/**
 * @fileoverview Filter Persistence Hook
 * 
 * This hook manages the persistence of filter state using localStorage
 * and database storage for filter configurations and presets.
 * 
 * Requirements: 3.5, 3.6, 10.3
 */

import { useCallback } from 'react';
import { useModuleContext } from '../../context/ModuleContextProvider.jsx';

/**
 * Filter Persistence Hook
 * 
 * @param {FilterConfig} config - Filter configuration
 * @returns {Object} Persistence methods
 */
export const useFilterPersistence = (config) => {
  const { apiClient, user } = useModuleContext();

  /**
   * Get storage key for localStorage
   */
  const getStorageKey = useCallback((suffix = '') => {
    const baseKey = config.storageKey || `filter-${config.context}`;
    return suffix ? `${baseKey}-${suffix}` : baseKey;
  }, [config.storageKey, config.context]);

  /**
   * Load filters from localStorage
   */
  const loadFiltersFromStorage = useCallback(() => {
    try {
      const storageKey = getStorageKey();
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const parsed = JSON.parse(stored);
        
        // Validate the stored data structure
        if (parsed && typeof parsed === 'object') {
          return {
            activeFilters: Array.isArray(parsed.activeFilters) ? parsed.activeFilters : [],
            presets: Array.isArray(parsed.presets) ? parsed.presets : [],
            activePreset: parsed.activePreset || null,
            lastUpdated: parsed.lastUpdated ? new Date(parsed.lastUpdated) : new Date()
          };
        }
      }
    } catch (error) {
      console.warn('Failed to load filters from localStorage:', error);
    }
    
    return null;
  }, [getStorageKey]);

  /**
   * Save filters to localStorage
   */
  const saveFiltersToStorage = useCallback((filterState) => {
    try {
      const storageKey = getStorageKey();
      const dataToStore = {
        activeFilters: filterState.activeFilters || [],
        presets: filterState.presets || [],
        activePreset: filterState.activePreset || null,
        lastUpdated: new Date().toISOString()
      };
      
      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
    } catch (error) {
      console.warn('Failed to save filters to localStorage:', error);
    }
  }, [getStorageKey]);

  /**
   * Load filters from database
   */
  const loadFiltersFromDatabase = useCallback(async () => {
    // Don't attempt database call if user is not available
    if (!user || !user.id) {
      console.debug('User not available for filter database operations');
      return null;
    }

    try {
      const response = await apiClient.get('/api/modules/filter/config', {
        params: {
          context: config.context,
          user_id: user.id
        }
      });

      if (response.success && response.data) {
        return {
          activeFilters: response.data.active_filters || [],
          presets: response.data.presets || [],
          activePreset: response.data.active_preset || null,
          lastUpdated: response.data.updated_at ? new Date(response.data.updated_at) : new Date()
        };
      }
    } catch (error) {
      // Handle 404 errors gracefully - this endpoint might not be implemented yet
      if (error.status === 404 || error.code === 'HTTP_404' || error.message?.includes('404')) {
        console.debug('Filter config endpoint not available (404) - using localStorage only');
      } else {
        console.warn('Failed to load filters from database:', error);
      }
    }
    
    return null;
  }, [apiClient, config.context, user.id]);

  /**
   * Save filters to database
   */
  const saveFiltersToDatabase = useCallback(async (filterState) => {
    // Don't attempt database call if user is not available
    if (!user || !user.id) {
      console.debug('User not available for filter database operations');
      return;
    }

    try {
      await apiClient.post('/api/modules/filter/config', {
        context: config.context,
        user_id: user.id,
        active_filters: filterState.activeFilters || [],
        presets: filterState.presets || [],
        active_preset: filterState.activePreset || null
      });
    } catch (error) {
      // Handle 404 errors gracefully - this endpoint might not be implemented yet
      if (error.status === 404 || error.code === 'HTTP_404' || error.message?.includes('404')) {
        console.debug('Filter config endpoint not available (404) - using localStorage only');
      } else {
        console.warn('Failed to save filters to database:', error);
      }
    }
  }, [apiClient, config.context, user.id]);

  /**
   * Load filters from both localStorage and database
   */
  const loadFilters = useCallback(async () => {
    // Try localStorage first for immediate response
    const localData = loadFiltersFromStorage();
    
    // If database persistence is enabled and user is available, try to load from database
    if (config.persistFilters && user && user.id) {
      const dbData = await loadFiltersFromDatabase();
      
      // Use database data if it's more recent, otherwise use local data
      if (dbData && localData) {
        const dbTime = new Date(dbData.lastUpdated).getTime();
        const localTime = new Date(localData.lastUpdated).getTime();
        
        if (dbTime > localTime) {
          // Database data is more recent, save it to localStorage too
          saveFiltersToStorage(dbData);
          return dbData;
        }
      } else if (dbData) {
        // Only database data available
        saveFiltersToStorage(dbData);
        return dbData;
      }
    }
    
    return localData;
  }, [config.persistFilters, user.id, loadFiltersFromStorage, loadFiltersFromDatabase, saveFiltersToStorage]);

  /**
   * Save filters to both localStorage and database
   */
  const saveFilters = useCallback(async (filterState) => {
    // Always save to localStorage for immediate persistence
    saveFiltersToStorage(filterState);
    
    // Save to database if enabled and user is authenticated
    if (config.persistFilters && user && user.id) {
      await saveFiltersToDatabase(filterState);
    }
  }, [config.persistFilters, user.id, saveFiltersToStorage, saveFiltersToDatabase]);

  /**
   * Clear filters from storage
   */
  const clearFilters = useCallback(async () => {
    try {
      // Clear localStorage
      const storageKey = getStorageKey();
      localStorage.removeItem(storageKey);
      
      // Clear database if enabled
      if (config.persistFilters && user && user.id) {
        await apiClient.delete('/api/modules/filter/config', {
          params: {
            context: config.context,
            user_id: user.id
          }
        });
      }
    } catch (error) {
      // Handle 404 errors gracefully - this endpoint might not be implemented yet
      if (error.status === 404 || error.code === 'HTTP_404' || error.message?.includes('404')) {
        console.debug('Filter config endpoint not available (404) - using localStorage only');
      } else {
        console.warn('Failed to clear filters:', error);
      }
    }
  }, [getStorageKey, config.persistFilters, config.context, user.id, apiClient]);

  /**
   * Load presets from database
   */
  const loadPresets = useCallback(async () => {
    // Don't attempt database call if user is not available
    if (!user || !user.id) {
      console.debug('User not available for preset database operations');
      return [];
    }

    try {
      const response = await apiClient.get('/api/modules/filter/presets', {
        params: {
          context: config.context,
          user_id: user.id
        }
      });

      if (response.success && response.data) {
        return response.data.presets || [];
      }
    } catch (error) {
      // Handle 404 errors gracefully - this endpoint might not be implemented yet
      if (error.status === 404 || error.code === 'HTTP_404' || error.message?.includes('404')) {
        console.debug('Filter presets endpoint not available (404) - using localStorage only');
      } else {
        console.warn('Failed to load presets from database:', error);
      }
    }
    
    return [];
  }, [apiClient, config.context, user.id]);

  /**
   * Save presets to database
   */
  const savePresets = useCallback(async (presets) => {
    try {
      // Save to localStorage
      const currentData = loadFiltersFromStorage() || {};
      saveFiltersToStorage({
        ...currentData,
        presets
      });
      
      // Save to database if enabled
      if (config.persistFilters && user && user.id) {
        await apiClient.post('/api/modules/filter/presets', {
          context: config.context,
          user_id: user.id,
          presets
        });
      }
    } catch (error) {
      // Handle 404 errors gracefully - this endpoint might not be implemented yet
      if (error.status === 404 || error.code === 'HTTP_404' || error.message?.includes('404')) {
        console.debug('Filter presets endpoint not available (404) - using localStorage only');
      } else {
        console.warn('Failed to save presets:', error);
      }
    }
  }, [config.persistFilters, config.context, user.id, apiClient, loadFiltersFromStorage, saveFiltersToStorage]);

  /**
   * Export filter configuration
   */
  const exportFilters = useCallback(() => {
    const currentData = loadFiltersFromStorage();
    if (!currentData) {
      return null;
    }

    return {
      context: config.context,
      activeFilters: currentData.activeFilters,
      presets: currentData.presets,
      exportedAt: new Date().toISOString(),
      version: '1.0.0',
      metadata: {
        userAgent: navigator.userAgent,
        timestamp: Date.now()
      }
    };
  }, [config.context, loadFiltersFromStorage]);

  /**
   * Import filter configuration
   */
  const importFilters = useCallback(async (importData) => {
    try {
      // Validate import data
      if (!importData || typeof importData !== 'object') {
        throw new Error('Invalid import data format');
      }

      if (importData.context !== config.context) {
        throw new Error(`Context mismatch: expected ${config.context}, got ${importData.context}`);
      }

      // Merge with existing data
      const currentData = loadFiltersFromStorage() || {
        activeFilters: [],
        presets: [],
        activePreset: null
      };

      const mergedData = {
        activeFilters: importData.activeFilters || currentData.activeFilters,
        presets: [
          ...currentData.presets,
          ...(importData.presets || [])
        ],
        activePreset: importData.activePreset || currentData.activePreset,
        lastUpdated: new Date()
      };

      // Save merged data
      await saveFilters(mergedData);
      
      return mergedData;
    } catch (error) {
      console.error('Failed to import filters:', error);
      throw error;
    }
  }, [config.context, loadFiltersFromStorage, saveFilters]);

  /**
   * Get storage statistics
   */
  const getStorageStats = useCallback(() => {
    const currentData = loadFiltersFromStorage();
    
    return {
      hasLocalData: !!currentData,
      activeFilterCount: currentData?.activeFilters?.length || 0,
      presetCount: currentData?.presets?.length || 0,
      lastUpdated: currentData?.lastUpdated || null,
      storageKey: getStorageKey()
    };
  }, [loadFiltersFromStorage, getStorageKey]);

  return {
    loadFilters,
    saveFilters,
    clearFilters,
    loadPresets,
    savePresets,
    exportFilters,
    importFilters,
    getStorageStats
  };
};
